import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "results",
    "annualSavings",
    "monthlySavings",
    "roiValue",
    "currentAgents",
    "cavalryAgents",
    "currentAgentCost",
    "cavalryAgentCost",
    "currentLaborCost",
    "cavalryLaborCost",
    "currentTickets",
    "cavalryTickets",
    "currentCostPerTicket",
    "cavalryCostPerTicket",
    "currentSupportCost",
    "cavalrySupportCost",
    "rangeInput",
    "textInput",
    "roiDetailsContainer",
    "contactUsContainer"
  ];

  static values = {
    tickets: { type: Number, default: 4000 },
    agents: { type: Number, default: 5 },
    cost: { type: Number, default: 12 },
    results: { type: Object, default: {} }
  };

  connect() {
    this.initializeInputs();
    this.hideResults();
  }

  initializeInputs() {
    this.rangeInputTargets.forEach(input => {
      const type = input.dataset.type;
      input.value = this[`${type}Value`];
      this.updateSliderFill(input);
    });
    
    this.textInputTargets.forEach(input => {
      const type = input.dataset.type;
      input.value = this.formatNumber(this[`${type}Value`]);
    });
  }

  hideResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.style.display = "none";
    }
  }
  
  updateValue(event) {
    const type = event.currentTarget.dataset.type;
    const value = parseInt(event.currentTarget.value);
    
    this[`${type}Value`] = value;
    
    const textInput = this.textInputTargets.find(input => input.dataset.type === type);
    if (textInput) {
      textInput.value = this.formatNumber(value);
    }
    
    this.updateSliderFill(event.currentTarget);
  }

  updateSlider(event) {
    const type = event.currentTarget.dataset.type;
    const inputField = event.currentTarget;
    let value = inputField.value.replace(/,/g, "");
    value = parseInt(value) || 0;
    
    this[`${type}Value`] = value;
    
    const slider = this.rangeInputTargets.find(input => input.dataset.type === type);
    if (slider) {
      const min = parseInt(slider.min) || 0;
      const max = parseInt(slider.max) || 50000;
      value = Math.max(min, Math.min(max, value));
      slider.value = value;
      this.updateSliderFill(slider);
    }
  }

  formatValue(event) {
    const type = event.currentTarget.dataset.type;
    const valueTarget = event.currentTarget;
    const slider = this.rangeInputTargets.find(input => input.dataset.type === type);

    if (!slider) return;
    
    let value = valueTarget.value.replace(/,/g, "");
    value = parseInt(value) || 0;

    const min = parseInt(slider.min) || 0;
    const max = parseInt(slider.max) || 50000;
    const boundedValue = Math.max(min, Math.min(max, value));
    
    this[`${type}Value`] = boundedValue;
    
    valueTarget.value = this.formatNumber(boundedValue);
    slider.value = boundedValue;
    this.updateSliderFill(slider);
  }
  
  ticketsValueChanged() {
    this.updateInputDisplay('tickets');
  }
  
  agentsValueChanged() {
    this.updateInputDisplay('agents');
  }
  
  costValueChanged() {
    this.updateInputDisplay('cost');
  }
  
  updateInputDisplay(type) {
    const slider = this.rangeInputTargets.find(input => input.dataset.type === type);
    const textInput = this.textInputTargets.find(input => input.dataset.type === type);
    
    if (slider) {
      slider.value = this[`${type}Value`];
      this.updateSliderFill(slider);
    }
    
    if (textInput) {
      textInput.value = this.formatNumber(this[`${type}Value`]);
    }
  }

  calculateRoi(event) {
    if (!this.hasResultsTarget) return;

    const numTickets = this.ticketsValue;
    const hourlyRate = this.costValue;
    const agentCostPerMonth = hourlyRate * 199.33;
    
    // Calculate number of agents needed based on ticket volume
    const currentAgents = this.agentsValue;
    
    // Calculate number of agents with Cavalry
    const cavalryAgents = currentAgents * 0.3;
    
    // Labor cost calculations
    const currentLaborCost = currentAgents * agentCostPerMonth;
    const cavalryLaborCost = cavalryAgents * agentCostPerMonth;

    // Support cost calculations
    const currentSupportCost = currentLaborCost;

    const currentTicketCost = currentSupportCost / numTickets;

    const costOfInvestment = numTickets * 0.7;

    // Total Support Cost / Month in With Cavalry
    const cavalrySupportCost = cavalryLaborCost + costOfInvestment;

    // Ticket Cost / Month in With Cavalry
    const cavalryTicketCost = cavalrySupportCost / numTickets;

    // Savings calculations
    const monthlySavings = currentSupportCost - cavalrySupportCost;
    const annualSavings = monthlySavings * 12;

    // ROI calculation
    const roi = annualSavings / (costOfInvestment * 12);

    this.resultsValue = {
      currentAgents,
      cavalryAgents,
      agentCostPerMonth,
      currentLaborCost,
      cavalryLaborCost,
      numTickets,
      currentTicketCost,
      cavalryTicketCost,
      currentSupportCost,
      cavalrySupportCost,
      monthlySavings,
      annualSavings,
      roi: roi * 100,
    };

    this.showResults();
  }
  
  resultsValueChanged() {
    this.updateResults(this.resultsValue);
  }
  
  showResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.style.display = "block";
      
      // Check if ROI is negative
      const roi = this.resultsValue.roi;
      
      if (roi < 0 && this.hasContactUsContainerTarget && this.hasRoiDetailsContainerTarget) {
        // Show only the contact us section
        this.contactUsContainerTarget.style.display = "block";
        this.roiDetailsContainerTarget.style.display = "none";
      } else if (this.hasContactUsContainerTarget && this.hasRoiDetailsContainerTarget) {
        // Show ROI details
        this.contactUsContainerTarget.style.display = "none";
        this.roiDetailsContainerTarget.style.display = "block";
      }
      
      this.resultsTarget.scrollIntoView({ behavior: "smooth" });
    }
  }

  updateResults(results) {
    if (!results || Object.keys(results).length === 0) return;
    
    const formatters = {
      standard: (val) => this.formatNumber(val),
      dollar: (val) => `$${this.formatNumberWithDecimals(val, 2)}`,
      dollarNoDecimal: (val) => `$${this.formatNumber(val.toFixed(0))}`,
      percent: (val) => `${val.toFixed(2)}%`,
      decimal: (val) => val.toFixed(2)
    };
    
    const updateMappings = [
      { target: 'annualSavings', value: results.annualSavings, formatter: formatters.dollarNoDecimal },
      { target: 'monthlySavings', value: results.monthlySavings, formatter: formatters.dollarNoDecimal },
      { target: 'roiValue', value: results.roi, formatter: formatters.percent },
      { target: 'currentAgents', value: results.currentAgents, formatter: formatters.standard },
      { target: 'cavalryAgents', value: results.cavalryAgents, formatter: formatters.decimal },
      { target: 'currentAgentCost', value: results.agentCostPerMonth, formatter: formatters.dollarNoDecimal },
      { target: 'cavalryAgentCost', value: results.agentCostPerMonth, formatter: formatters.dollarNoDecimal },
      { target: 'currentLaborCost', value: results.currentLaborCost, formatter: formatters.dollarNoDecimal },
      { target: 'cavalryLaborCost', value: results.cavalryLaborCost, formatter: formatters.dollarNoDecimal },
      { target: 'currentTickets', value: results.numTickets, formatter: formatters.standard },
      { target: 'cavalryTickets', value: results.numTickets, formatter: formatters.standard },
      { target: 'currentCostPerTicket', value: results.currentTicketCost, formatter: formatters.dollar },
      { target: 'cavalryCostPerTicket', value: results.cavalryTicketCost, formatter: formatters.dollar },
      { target: 'currentSupportCost', value: results.currentSupportCost, formatter: formatters.dollarNoDecimal },
      { target: 'cavalrySupportCost', value: results.cavalrySupportCost, formatter: formatters.dollarNoDecimal },
    ];
    
    updateMappings.forEach(({ target, value, formatter }) => {
      const targetElement = this[`${target}Target`];
      if (targetElement) {
        targetElement.textContent = formatter(value);
      }
    });
  }

  formatNumber(number) {
    return new Intl.NumberFormat("en-US").format(number);
  }

  formatNumberWithDecimals(number, decimals = 2) {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(number);
  }

  updateSliderFill(slider) {
    const min = parseInt(slider.min) || 0;
    const max = parseInt(slider.max) || 50000;
    const value = parseInt(slider.value) || 0;

    const percentage = ((value - min) / (max - min)) * 100;

    slider.style.setProperty("--slider-fill", `${percentage}%`);
  }
}
